<template>
  <div class="pg-adp">
    <!-- 섹션 레벨: 제목 -->
    <div class="p-field">
      <label class="p-field__label">Section Title</label>
      <input
        v-model="sectionData.content.sectionTitle"
        type="text"
        class="p-input"
        placeholder="섹션 제목"
      />
    </div>

    <!-- 문항 탭 -->
    <question-tabs
      :count="questions.length"
      v-model="currentIndex"
      @add="addQuestion"
      @remove="removeQuestion"
    />

    <template v-if="current">
      <div class="p-form-grid p-form-grid--2">
        <div class="p-field">
          <label class="p-field__label">Question / 지시문</label>
          <input
            v-model="current.questionText"
            type="text"
            class="p-input"
            placeholder="예: 음성을 듣고 알맞은 위치로 드래그하세요"
          />
        </div>
        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round pg-adp__audio-ico">music_note</i>
            Audio URL (위치 발음)
          </label>
          <input
            v-model="current.audioUrl"
            type="text"
            class="p-input"
            placeholder="음성 URL (풀 URL 권장)"
          />
        </div>
      </div>

      <div class="p-field">
        <label class="p-field__label">음성 스크립트 (선택 · 예: "침대 옆")</label>
        <input
          v-model="current.audioScript"
          type="text"
          class="p-input"
          placeholder="음성 내용 표기 (미리보기·참고용)"
        />
      </div>

      <div class="p-form-grid p-form-grid--2">
        <!-- 기준 사물 (고정) -->
        <div class="pg-adp__asset">
          <div class="p-field">
            <label class="p-field__label">기준 사물 이름 (고정 · 예: 침대)</label>
            <input
              v-model="current.baseLabel"
              type="text"
              class="p-input"
              placeholder="예: 침대"
            />
          </div>
          <div class="p-field">
            <label class="p-field__label">기준 사물 삽화 (고정)</label>
            <image-upload
              :default-image="current.baseImage || require('@/assets/img/no_image.png')"
              :unique-id="'adp-base-' + currentIndex"
              v-model="current.baseImage"
            />
          </div>
        </div>
        <!-- 드래그 아이템 -->
        <div class="pg-adp__asset">
          <div class="p-field">
            <label class="p-field__label">드래그 아이템 이름 (예: 공)</label>
            <input
              v-model="current.dragLabel"
              type="text"
              class="p-input"
              placeholder="예: 공"
            />
          </div>
          <div class="p-field">
            <label class="p-field__label">드래그 아이템 삽화</label>
            <image-upload
              :default-image="current.dragImage || require('@/assets/img/no_image.png')"
              :unique-id="'adp-drag-' + currentIndex"
              v-model="current.dragImage"
            />
          </div>
        </div>
      </div>

      <!-- 정답 요약 미리보기 -->
      <div class="pg-adp__preview">
        <span class="pg-adp__preview-label">정답 위치</span>
        <span class="pg-adp__preview-text">{{ answerSummary }}</span>
      </div>

      <!-- 위치 드롭존 -->
      <div class="pg-adp__zones">
        <div
          v-for="(pos, index) in current.positions"
          :key="index"
          class="pg-adp__item"
          :class="{ 'is-answer': current.answer === index }"
        >
          <div class="pg-adp__item-head">
            <label class="pg-adp__answer" :class="{ 'is-on': current.answer === index }">
              <input
                type="radio"
                :name="answerRadioName"
                :value="index"
                v-model="current.answer"
              />
              <i class="material-icons-round">{{ current.answer === index ? 'check_circle' : 'radio_button_unchecked' }}</i>
              <span>정답</span>
            </label>
            <span class="pg-adp__index">위치 {{ index + 1 }}</span>
            <button
              type="button"
              class="pg-adp__icon-btn pg-adp__icon-btn--danger"
              title="제거"
              @click.prevent="removePosition(index)"
            >
              <i class="material-icons-round">delete</i>
            </button>
          </div>
          <div class="pg-adp__item-body">
            <div class="p-field">
              <label class="p-field__label">위치 라벨 (예: 옆 / 위 / 아래 / 뒤)</label>
              <input
                v-model="pos.label"
                type="text"
                class="p-input"
                placeholder="위치 라벨"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="pg-adp__actions">
        <ABtn kind="yellow" icon="add" @click.prevent="addPosition">위치 추가</ABtn>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed } from "vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import ImageUpload from "@/components/ImageUpload.vue";
import QuestionTabs from "@/components/Practice/QuestionTabs.vue";
import { useQuestions } from "@/components/Practice/useQuestions";

const sectionData = defineModel({ type: Object, required: true });

const makeEmpty = () => ({
  questionText: "",
  audioUrl: "",
  audioScript: "",
  baseLabel: "",
  baseImage: "",
  dragLabel: "",
  dragImage: "",
  positions: [{ label: "" }],
  answer: null
});

const { questions, currentIndex, current, addQuestion, removeQuestion } = useQuestions(
  sectionData,
  makeEmpty,
  ["questionText", "audioUrl", "audioScript", "baseLabel", "baseImage", "dragLabel", "dragImage", "positions", "answer"]
);

const answerRadioName = computed(() => `adp-answer-${currentIndex.value}`);

const answerSummary = computed(() => {
  if (!current.value) return "";
  const answer = current.value.answer;
  const positions = current.value.positions || [];
  if (typeof answer === "number" && positions[answer]) {
    return positions[answer].label || "(라벨 없음)";
  }
  return "정답 위치를 지정하세요";
});

const addPosition = () => {
  current.value.positions.push({ label: "" });
};

const removePosition = (index) => {
  current.value.positions.splice(index, 1);
  const answer = current.value.answer;
  if (answer === index) {
    current.value.answer = null;
  } else if (typeof answer === "number" && answer > index) {
    current.value.answer = answer - 1;
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.pg-adp {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__audio-ico {
    font-size: 14px !important;
    color: $p-point-deep;
    vertical-align: middle;
    margin-right: 4px;
  }

  &__asset {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  // ── 정답 미리보기 ──
  &__preview {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 14px;
    border: 1px dashed $p-line-hard;
    border-radius: $p-radius-md;
    background: $p-bg;
  }

  &__preview-label {
    font-size: 10.5px;
    font-weight: 800;
    letter-spacing: 0.05em;
    color: $p-ink-soft;
    text-transform: uppercase;
    flex-shrink: 0;
  }

  &__preview-text {
    font-size: 15px;
    font-weight: 600;
    color: $p-ink;
  }

  // ── 위치 드롭존 ──
  &__zones {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__item {
    background: $p-panel;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    padding: 14px 16px;
    transition: border-color $p-dur $p-ease, background $p-dur $p-ease;

    &:hover { border-color: $p-line-hard; }

    &.is-answer {
      border-color: $p-ok;
      background: $p-ok-soft;
    }
  }

  &__item-head {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
    padding-bottom: 10px;
    border-bottom: 1px dashed $p-line;
  }

  &__answer {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    cursor: pointer;
    font-size: 12px;
    font-weight: 700;
    color: $p-ink-mid;
    user-select: none;

    > input { display: none; }
    > i { font-size: 18px; color: $p-ink-soft; }

    &.is-on {
      color: $p-ok;
      > i { color: $p-ok; }
    }
  }

  &__index {
    margin-left: auto;
    padding: 4px 12px;
    border-radius: 999px;
    background: $p-ink;
    color: $p-point;
    font-family: $p-font-mono;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.04em;
  }

  &__icon-btn {
    width: 30px;
    height: 30px;
    border: 0;
    background: transparent;
    color: $p-ink-mid;
    border-radius: $p-radius-sm;
    display: grid;
    place-items: center;
    cursor: pointer;
    transition: background $p-dur $p-ease, color $p-dur $p-ease;

    > i { font-size: 16px; }

    &--danger {
      color: $p-err;
      &:hover { background: $p-err-soft; }
    }
  }

  &__item-body {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__actions {
    display: flex;
    gap: 8px;
  }
}
</style>
